import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, Calendar, Star, DollarSign, ArrowLeft, 
  TrendingUp, Clock, CheckCircle, XCircle, Loader2,
  Shield, UserCheck, AlertCircle, FileText, Search, Download, Mail, Phone, Trash2, ShieldCheck, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";
import DocumentViewerModal from "../components/DocumentViewerModal";
import UserDetailModal from "../components/UserDetailModal";
import AdminAnalytics from "../components/AdminAnalytics";
import AdminFinance from "../components/AdminFinance";
import { AdminBookingsManager } from "../components/AdminBookingsManager";
import { OperationsGuide } from "../components/OperationsGuide";
import { AdminIncidentsManager } from "../components/AdminIncidentsManager";
import { AdminInvoicingManager } from "../components/AdminInvoicingManager";
import AdminApplicationsManager from "../components/AdminApplicationsManager";
import { AdminServiceAgreementBuilder } from "../components/AdminServiceAgreementBuilder";
import { AdminUserCreator } from "../components/AdminUserCreator";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/6i1oj5oc_BTR_Health_favicon__2_-removebg-preview.png";

function AdminDocumentsReview() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [viewerOwner, setViewerOwner] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/admin/workers/full`);
    if (res.ok) setWorkers(await res.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const review = async (docId, action) => {
    const reason = action !== "approve" ? (window.prompt("Reason / instructions for the worker") || "") : "";
    const res = await authFetch(`${API}/admin/documents/${docId}/review`, { method: "PUT", body: JSON.stringify({ action, reason }) });
    if (res.ok) { toast.success(`Document ${action}d`); load(); }
    else { toast.error("Action failed"); }
  };

  // Review from inside the viewer modal — returns true on success so the modal can close.
  const reviewFromViewer = async (action, reason) => {
    if (!viewerDoc) return false;
    const res = await authFetch(`${API}/admin/documents/${viewerDoc.doc_id}/review`, {
      method: "PUT",
      body: JSON.stringify({ action, reason })
    });
    if (res.ok) {
      toast.success(`Document ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "resubmit requested"}`);
      await load();
      return true;
    }
    toast.error("Action failed");
    return false;
  };

  const approveWorker = async (workerId, action) => {
    const reason = action === "deny" ? (window.prompt("Reason for denial (sent to worker)") || "") : "";
    const res = await authFetch(`${API}/admin/workers/${workerId}/approval`, { method: "PUT", body: JSON.stringify({ action, reason }) });
    if (res.ok) { toast.success(`Worker ${action}d`); load(); }
    else { toast.error("Action failed"); }
  };

  const setVisibility = async (workerId, hidden) => {
    if (hidden && !window.confirm("Hide this worker from the public directory? Their data will be preserved and you can show them again at any time.")) return;
    const res = await authFetch(`${API}/admin/workers/${workerId}/visibility`, { method: "PUT", body: JSON.stringify({ hidden }) });
    if (res.ok) { toast.success(hidden ? "Worker hidden from website" : "Worker is now visible"); load(); }
    else { toast.error("Action failed"); }
  };

  const downloadFile = (filename, content, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadWorkerProfile = async (worker, format = "json") => {
    try {
      const res = await authFetch(`${API}/admin/workers/${worker.worker_id}/export`);
      if (!res.ok) throw new Error("Export failed");
      const data = await res.json();
      const safeName = (worker.name || "worker").replace(/[^a-z0-9]+/gi, "_");
      if (format === "json") {
        downloadFile(`${safeName}_full_profile.json`, JSON.stringify(data, null, 2), "application/json");
        toast.success("Profile downloaded as JSON");
        return;
      }
      // HTML: open a printable, full-detail report in a new tab (user can Save as PDF)
      const w = data.worker || {};
      const u = data.user || {};
      const docs = data.documents || [];
      const ag = data.subcontractor_agreement;
      const escape = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      const row = (k, v) => `<tr><th align="left" style="padding:6px 12px;background:#f5f9fb;color:#475569;font-weight:600;width:220px">${escape(k)}</th><td style="padding:6px 12px;color:#0f172a">${v == null || v === "" ? "—" : escape(v)}</td></tr>`;
      const refsHtml = (w.references || []).map((r, i) => row(`Reference ${i + 1}`, `${r.name || "—"} · ${r.phone || ""} · ${r.relationship || ""}`)).join("");
      const docsHtml = docs.map((d) => `
        <div style="border:1px solid #e2e8f0;border-radius:12px;padding:14px;margin-bottom:10px">
          <div style="display:flex;justify-content:space-between;margin-bottom:6px"><strong>${escape((d.doc_type || "").replace(/_/g, " "))}</strong><span style="background:${d.status === 'verified' ? '#dcfce7' : '#fef3c7'};color:#0f172a;padding:2px 8px;border-radius:999px;font-size:11px">${escape(d.status || "pending")}</span></div>
          <div style="font-size:12px;color:#475569">File: ${escape(d.file_name || "—")} · Uploaded: ${escape(d.uploaded_at || "—")}${d.expiry_date ? ` · Expires: ${escape(d.expiry_date)}` : ""}${d.number ? ` · # ${escape(d.number)}` : ""}</div>
          ${d.file_url && d.file_url.startsWith("data:image") ? `<img src="${d.file_url}" style="max-width:100%;border:1px solid #e2e8f0;border-radius:8px;margin-top:8px"/>` : d.file_url ? `<a href="${d.file_url}" target="_blank">Open file</a>` : ""}
        </div>`).join("");
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escape(w.name)} — Worker Profile</title><style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#0f172a;max-width:880px;margin:24px auto;padding:0 16px}h1{font-size:22px;margin:0 0 4px}h2{font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#5BA3C0;margin:28px 0 8px;border-bottom:1px solid #e2e8f0;padding-bottom:6px}table{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;margin-bottom:8px}img.avatar{width:96px;height:96px;border-radius:50%;object-fit:cover;border:3px solid #87CEEB}@media print{.no-print{display:none}}</style></head><body>
        <div style="display:flex;gap:16px;align-items:center">
          ${w.picture ? `<img class="avatar" src="${escape(w.picture)}"/>` : ""}
          <div>
            <h1>${escape(w.name)}</h1>
            <div style="color:#475569;font-size:13px">${escape(w.email || "")} · ${escape(w.phone || "")} · ${escape((w.worker_type || "").toUpperCase())}</div>
            <div style="margin-top:6px"><span style="background:#eaf6fc;color:#5BA3C0;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600">REGISTERED NDIS PROVIDER · BTR Health</span></div>
          </div>
          <div class="no-print" style="margin-left:auto"><button onclick="window.print()" style="background:#0f172a;color:#fff;padding:8px 14px;border:0;border-radius:8px;cursor:pointer">Save as PDF</button></div>
        </div>
        <h2>Personal</h2><table>
          ${row("Date of birth", w.date_of_birth || u.dob)}
          ${row("Address", [w.address_street, w.location, w.state, w.address_postcode].filter(Boolean).join(", "))}
          ${row("ABN", w.abn)}
          ${row("Worker type", w.worker_type)}
        </table>
        <h2>Services & Rate</h2><table>
          ${row("Services", (w.services || w.skills || []).join(", "))}
          ${row("Languages", w.languages)}
          ${row("Hourly rate", w.hourly_rate ? `$${w.hourly_rate}/hr` : "")}
          ${row("Experience", w.experience)}
          ${row("Travel radius (km)", w.travel_radius_km)}
          ${row("Bio", w.bio)}
        </table>
        <h2>Emergency contact</h2><table>
          ${row("Name", w.emergency_contact_name)}
          ${row("Phone", w.emergency_contact_phone)}
          ${row("Relationship", w.emergency_contact_relationship)}
        </table>
        <h2>Payment & superannuation</h2><table>
          ${row("BSB", w.bank_bsb)}
          ${row("Account number", w.bank_account_number)}
          ${row("Account name", w.bank_account_name)}
          ${row("Super fund", w.super_fund)}
          ${row("Member number", w.super_member_number)}
        </table>
        <h2>References</h2><table>${refsHtml || row("References", "—")}</table>
        <h2>Subcontractor Agreement</h2><table>
          ${ag ? `
            ${row("Version", ag.version)}
            ${row("Signed name", ag.signed_name)}
            ${row("Signed at", ag.signed_at)}
            ${row("IP address", ag.ip)}
            ${row("User agent", ag.user_agent)}
            ${row("ABN at signing", ag.abn_at_signing)}
            ${row("DOB at signing", ag.dob_at_signing)}
            ${row("Address at signing", ag.address_at_signing)}
          ` : row("Status", "Not yet signed")}
        </table>
        <h2>Verification documents (${docs.length})</h2>${docsHtml || "<p>No documents uploaded yet.</p>"}
        <p style="margin-top:32px;color:#94a3b8;font-size:11px">Exported ${escape(data.exported_at)} · BTR Health Pty Ltd (Registered NDIS Provider)</p>
      </body></html>`;
      const win = window.open("", "_blank");
      if (win) { win.document.write(html); win.document.close(); }
      else { downloadFile(`${safeName}_full_profile.html`, html, "text/html"); }
      toast.success("Printable profile opened");
    } catch (err) {
      console.error("[admin] export failed", err);
      toast.error("Could not export worker profile");
    }
  };

  const workersWithDocs = workers;

  if (loading) return <p className="text-slate-400 p-6">Loading…</p>;
  if (workersWithDocs.length === 0) return <p className="text-slate-500 p-6">No workers yet.</p>;

  return (
    <div className="space-y-4" data-testid="admin-documents-review">
      {workersWithDocs.map(w => (
        <div key={w.worker_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-slate-900">{w.name}</h3>
              <p className="text-xs text-slate-500">{w.email} · {w.location} · {(w.worker_type || "independent").toUpperCase()}</p>
              {w.subcontractor_agreement?.signed_at && (
                <p className="text-[11px] text-emerald-700 mt-1">✓ Subcontractor Agreement signed {new Date(w.subcontractor_agreement.signed_at).toLocaleDateString("en-AU")} ({w.subcontractor_agreement.version})</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge className={
                w.approval_status === "approved" ? "bg-emerald-100 text-emerald-700"
                : w.approval_status === "denied" ? "bg-red-100 text-red-700"
                : "bg-amber-100 text-amber-700"
              }>
                {(w.approval_status || "pending").toUpperCase()}
              </Badge>
              {w.hidden_from_directory && (
                <Badge className="bg-slate-200 text-slate-700">HIDDEN</Badge>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => downloadWorkerProfile(w, "html")}
                data-testid={`download-profile-${w.worker_id}`}
                title="Open a printable profile (Save as PDF from your browser)"
              >
                <FileText className="w-3 h-3 mr-1" /> Printable profile
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => downloadWorkerProfile(w, "json")}
                data-testid={`download-json-${w.worker_id}`}
                title="Download all worker data as JSON"
              >
                <Download className="w-3 h-3 mr-1" /> JSON
              </Button>
              {w.approval_status !== "approved" && (
                <Button size="sm" className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => approveWorker(w.worker_id, "approve")} data-testid={`approve-worker-${w.worker_id}`}>Approve worker</Button>
              )}
              {w.approval_status !== "denied" && (
                <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600" onClick={() => approveWorker(w.worker_id, "deny")} data-testid={`deny-worker-${w.worker_id}`}>Deny</Button>
              )}
              {w.hidden_from_directory ? (
                <Button size="sm" variant="outline" className="h-8 text-xs text-emerald-700" onClick={() => setVisibility(w.worker_id, false)} data-testid={`show-worker-${w.worker_id}`}>Show on website</Button>
              ) : (
                <Button size="sm" variant="outline" className="h-8 text-xs text-slate-700" onClick={() => setVisibility(w.worker_id, true)} data-testid={`hide-worker-${w.worker_id}`}>Hide from website</Button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {(w.documents || []).length === 0 && (
              <p className="text-xs text-slate-400">No documents uploaded yet.</p>
            )}
            {(w.documents || []).map(d => (
              <div key={d.doc_id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{d.doc_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                  <p className="text-xs text-slate-500">
                    Status: <span className="font-medium">{d.status}</span>
                    {d.number && ` · #${d.number}`}
                    {d.expiry_date && ` · Expires ${d.expiry_date}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-[#87CEEB] hover:bg-[#6BB8DB] text-white"
                    onClick={() => { setViewerOwner(w.name); setViewerDoc(d); }}
                    data-testid={`view-doc-${d.doc_id}`}
                    disabled={!d.file_url}
                    title={d.file_url ? "Open the document to view and review" : "No file uploaded yet"}
                  >
                    View & review
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => review(d.doc_id, "approve")} data-testid={`approve-${d.doc_id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600" onClick={() => review(d.doc_id, "reject")} data-testid={`reject-${d.doc_id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <DocumentViewerModal
        open={!!viewerDoc}
        doc={viewerDoc}
        ownerName={viewerOwner}
        onClose={() => setViewerDoc(null)}
        onReview={reviewFromViewer}
      />
    </div>
  );
}

function AdminParticipantDocsReview() {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [viewerOwner, setViewerOwner] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/admin/participants/full`);
    if (res.ok) setParticipants(await res.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const review = async (docId, action) => {
    const reason = action !== "approve" ? (window.prompt("Reason for the participant") || "") : "";
    const res = await authFetch(`${API}/admin/participant-documents/${docId}/review`, { method: "PUT", body: JSON.stringify({ action, reason }) });
    if (res.ok) { toast.success(`Document ${action}d`); load(); }
    else { toast.error("Action failed"); }
  };

  const reviewFromViewer = async (action, reason) => {
    if (!viewerDoc) return false;
    const res = await authFetch(`${API}/admin/participant-documents/${viewerDoc.doc_id}/review`, {
      method: "PUT",
      body: JSON.stringify({ action, reason })
    });
    if (res.ok) {
      toast.success(`Document ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "resubmit requested"}`);
      await load();
      return true;
    }
    toast.error("Action failed");
    return false;
  };

  const withDocs = participants.filter(p => (p.documents || []).length > 0);

  if (loading) return <p className="text-slate-400 p-6">Loading…</p>;
  if (withDocs.length === 0) return <p className="text-slate-500 p-6">No participant documents uploaded yet.</p>;

  return (
    <div className="space-y-4" data-testid="admin-participants-review">
      {withDocs.map(p => (
        <div key={p.user_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-slate-900">{p.name}</h3>
              <p className="text-xs text-slate-500">{p.email} · {p.location || "-"} · NDIS {p.ndis_number || "-"} · {(p.ndis_plan_type || "unspecified").replace(/_/g,' ')}</p>
            </div>
            <Badge className={p.participant_verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
              {p.participant_verified ? "FULLY VERIFIED" : "PENDING"}
            </Badge>
          </div>
          <div className="space-y-2">
            {(p.documents || []).map(d => (
              <div key={d.doc_id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{d.doc_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                  <p className="text-xs text-slate-500">Status: <span className="font-medium">{d.status}</span>{d.expiry_date && ` · Expires ${d.expiry_date}`}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-[#87CEEB] hover:bg-[#6BB8DB] text-white"
                    onClick={() => { setViewerOwner(p.name); setViewerDoc(d); }}
                    data-testid={`p-view-doc-${d.doc_id}`}
                    disabled={!d.file_url}
                    title={d.file_url ? "Open the document to view and review" : "No file uploaded yet"}
                  >
                    View & review
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => review(d.doc_id, "approve")} data-testid={`p-approve-${d.doc_id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600" onClick={() => review(d.doc_id, "reject")} data-testid={`p-reject-${d.doc_id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <DocumentViewerModal
        open={!!viewerDoc}
        doc={viewerDoc}
        ownerName={viewerOwner}
        onClose={() => setViewerDoc(null)}
        onReview={reviewFromViewer}
      />
    </div>
  );
}

function AdminPricingReference() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await authFetch(`${API}/pricing/categories`);
      if (r.ok) setData(await r.json());
      setLoading(false);
    })();
  }, []);

  if (loading) return <p className="text-slate-400 p-6">Loading NDIS pricing reference…</p>;
  if (!data) return <p className="text-slate-500 p-6">Could not load pricing data.</p>;

  const q = query.trim().toLowerCase();
  const matchesQuery = (g) => !q || g.label.toLowerCase().includes(q) ||
    g.line_items.some(li => (li.name || "").toLowerCase().includes(q) || (li.code || "").includes(q));

  const planBadge = (label, on) => (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400 line-through"}`}>{label}</span>
  );

  return (
    <div className="space-y-6" data-testid="admin-pricing-reference">
      <div>
        <h3 className="text-base font-bold text-slate-900">Services &amp; pricing (internal training)</h3>
        <p className="text-xs text-slate-500">Live reference for what BTR Health can deliver, to whom, and at what rate. Source: NDIS Pricing Arrangements &amp; Price Limits {data.financial_year}.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search category, line item, or NDIS support number…"
          className="h-9 pl-9 text-sm"
          data-testid="pricing-search"
        />
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-900 mb-3">✓ What we CAN service ({data.registration_groups.length} registration groups)</h4>
        <div className="space-y-2">
          {data.registration_groups.filter(matchesQuery).map(g => {
            const isOpen = openKey === g.key;
            const standardWk = g.line_items.find(li => (li.name || "").includes("Standard") && (li.name || "").includes("Daytime"))?.weekday || g.line_items[0]?.weekday;
            return (
              <div key={g.key} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`pricing-group-${g.key}`}>
                <button onClick={() => setOpenKey(isOpen ? null : g.key)} className="w-full px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/50 text-left" data-testid={`pricing-group-toggle-${g.key}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-900">{g.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{g.summary}</p>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {planBadge("Self", g.can_service.self)}
                      {planBadge("Plan", g.can_service.plan_managed)}
                      {planBadge("NDIA", g.can_service.ndia_managed)}
                      {planBadge("Private", g.can_service.private)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Weekday cap</p>
                    <p className="font-bold text-slate-900">{standardWk ? `$${Number(standardWk).toFixed(2)}` : <span className="text-slate-400 italic font-normal">Quotable</span>}</p>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/30">
                    <p className="text-xs text-slate-500 mb-3">{g.summary}</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-wider text-slate-400">
                          <tr className="border-b border-slate-200">
                            <th className="text-left pb-2">Line item</th>
                            <th className="text-left pb-2">Support #</th>
                            <th className="text-right pb-2">Weekday</th>
                            <th className="text-right pb-2">Evening</th>
                            <th className="text-right pb-2">Saturday</th>
                            <th className="text-right pb-2">Sunday</th>
                            <th className="text-right pb-2">P. Holiday</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {g.line_items.map(li => (
                            <tr key={li.code}>
                              <td className="py-2 text-slate-700">{li.name}</td>
                              <td className="py-2 font-mono text-[10px] text-slate-500">{li.code}</td>
                              <td className="py-2 text-right font-medium text-slate-900">{li.weekday != null ? `$${li.weekday.toFixed(2)}` : (li.unit || "—")}</td>
                              <td className="py-2 text-right text-slate-700">{li.evening != null ? `$${li.evening.toFixed(2)}` : "—"}</td>
                              <td className="py-2 text-right text-slate-700">{li.saturday != null ? `$${li.saturday.toFixed(2)}` : "—"}</td>
                              <td className="py-2 text-right text-slate-700">{li.sunday != null ? `$${li.sunday.toFixed(2)}` : "—"}</td>
                              <td className="py-2 text-right text-slate-700">{li.public_holiday != null ? `$${li.public_holiday.toFixed(2)}` : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-900 mb-3">✗ What we CAN'T service yet ({data.not_registered_for.length} categories)</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.not_registered_for.map(x => (
            <div key={x.label} className="bg-rose-50/40 border border-rose-200/60 rounded-2xl p-4" data-testid={`pricing-blocked-${x.label.replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="w-4 h-4 text-rose-500 shrink-0" />
                <p className="font-bold text-slate-900 text-sm">{x.label}</p>
              </div>
              <p className="text-xs text-slate-600 mb-2"><span className="font-semibold">Examples:</span> {x.examples}</p>
              <p className="text-xs text-rose-700 mb-2"><span className="font-semibold">Blocked for:</span> {x.blocked_for}</p>
              <p className="text-xs text-slate-700"><span className="font-semibold">To unlock:</span> {x.to_unlock}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function AdminIdVerifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("pending_review");
  const [rejectModal, setRejectModal] = useState(null); // { user_id, name }
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/id-verifications`);
    if (r.ok) setRows(await r.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const review = async (userId, action, reason = null) => {
    setBusyId(userId);
    const r = await authFetch(`${API}/admin/id-verifications/${userId}/review`, {
      method: "POST",
      body: JSON.stringify({ action, reason }),
    });
    if (r.ok) {
      toast.success(`Verification ${action}d`);
      await load();
    } else {
      toast.error("Action failed");
    }
    setBusyId(null);
  };

  if (loading) return <p className="text-slate-400 p-6">Loading ID verifications…</p>;

  const counts = {
    pending_review: rows.filter(r => r.id_verification_status === "pending_review").length,
    verified: rows.filter(r => r.id_verification_status === "verified").length,
    rejected: rows.filter(r => r.id_verification_status === "rejected").length,
  };
  const filtered = rows.filter(r => r.id_verification_status === filter);

  const idLabel = (t) => ({
    drivers_licence: "Driver's licence",
    medicare: "Medicare card",
    passport: "Passport",
  })[t] || (t || "—").replace(/_/g, " ");

  return (
    <div className="space-y-4" data-testid="admin-id-verifications">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">Participant ID verifications</h3>
          <p className="text-xs text-slate-500">Review government-issued ID details and approve or reject. Participants can't book a worker until they're verified.</p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
          {[
            { k: "pending_review", label: `Pending (${counts.pending_review})`, c: "text-amber-700" },
            { k: "verified", label: `Verified (${counts.verified})`, c: "text-emerald-700" },
            { k: "rejected", label: `Rejected (${counts.rejected})`, c: "text-rose-700" },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${filter === t.k ? "bg-white shadow-sm " + t.c : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`idv-filter-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-8 text-center">
          <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No {filter.replace(/_/g, " ")} verifications.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const d = r.id_verification_data || {};
            return (
              <div key={r.user_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid={`idv-row-${r.user_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-slate-900">{r.name}</h4>
                      <Badge className={
                        r.id_verification_status === "pending_review" ? "bg-amber-100 text-amber-700" :
                        r.id_verification_status === "verified" ? "bg-emerald-100 text-emerald-700" :
                        "bg-rose-100 text-rose-700"
                      }>
                        {r.id_verification_status.replace(/_/g, " ").toUpperCase()}
                      </Badge>
                      <span className="text-xs text-slate-500">Attempt {r.id_verification_attempts}/5</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                  </div>
                  <p className="text-xs text-slate-500">
                    Submitted {r.id_verification_submitted_at ? new Date(r.id_verification_submitted_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                  </p>
                </div>

                <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  <Field k="ID type" v={idLabel(d.id_type)} />
                  <Field k="Legal name" v={d.legal_name} />
                  <Field k="Date of birth" v={d.date_of_birth} />
                  <Field k="Document number" v={d.document_number} mono />
                  {d.id_type === "drivers_licence" && <>
                    <Field k="State of issue" v={d.licence_state} />
                    <Field k="Card number" v={d.licence_card_number} mono />
                  </>}
                  {d.id_type === "medicare" && <>
                    <Field k="Position (IRN)" v={d.medicare_position} />
                    <Field k="Card colour" v={d.medicare_colour && d.medicare_colour[0].toUpperCase() + d.medicare_colour.slice(1)} />
                    <Field k="Expiry" v={d.medicare_expiry} />
                  </>}
                </div>

                {r.id_verification_rejection_reason && (
                  <div className="mt-3 p-2.5 rounded-lg bg-rose-50 text-rose-800 text-xs">
                    <strong>Rejection reason:</strong> {r.id_verification_rejection_reason}
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {r.id_verification_status === "pending_review" && (
                    <>
                      <Button
                        size="sm"
                        className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white"
                        disabled={busyId === r.user_id}
                        onClick={() => review(r.user_id, "approve")}
                        data-testid={`idv-approve-${r.user_id}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-rose-600 border-rose-200"
                        disabled={busyId === r.user_id}
                        onClick={() => { setRejectModal({ user_id: r.user_id, name: r.name }); setRejectReason(""); }}
                        data-testid={`idv-reject-${r.user_id}`}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {r.id_verification_status !== "not_started" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs text-slate-500"
                      disabled={busyId === r.user_id}
                      onClick={() => { if (window.confirm(`Reset ${r.name}'s verification? They'll be able to re-submit from scratch.`)) review(r.user_id, "reset"); }}
                      data-testid={`idv-reset-${r.user_id}`}
                    >
                      Reset
                    </Button>
                  )}
                  {r.id_verification_reviewed_by && (
                    <span className="text-[11px] text-slate-400 ml-auto">Reviewed by {r.id_verification_reviewed_by}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reject modal */}
      <Dialog open={!!rejectModal} onOpenChange={(open) => !open && setRejectModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject {rejectModal?.name}'s ID verification?</DialogTitle>
            <DialogDescription>The participant will be emailed with the reason and can try again.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. The name on your licence doesn't match the account name — please use the legal name printed on your ID."
            rows={4}
            data-testid="idv-reject-reason"
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setRejectModal(null)}>Cancel</Button>
            <Button
              className="bg-rose-500 hover:bg-rose-600 text-white"
              disabled={!rejectReason.trim()}
              onClick={async () => {
                const id = rejectModal.user_id;
                setRejectModal(null);
                await review(id, "reject", rejectReason.trim());
              }}
              data-testid="idv-reject-confirm"
            >
              Send rejection
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ k, v, mono }) => (
  <div className="flex gap-2">
    <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider w-32 shrink-0">{k}</span>
    <span className={`text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{v || <span className="text-slate-300 italic font-sans">—</span>}</span>
  </div>
);


function AdminSignedAgreementsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/admin/signed-agreements`);
    if (res.ok) setRows(await res.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const download = async (userId, name) => {
    setBusyId(`dl-${userId}`);
    try {
      const res = await authFetch(`${API}/admin/signed-agreements/${userId}/pdf`);
      if (!res.ok) {
        toast.error("Download failed");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BTR-Health-Service-Agreement-${(name || "participant").replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Agreement downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setBusyId(null);
    }
  };

  const resendEmail = async (userId, email) => {
    if (!window.confirm(`Re-send the signed PDF to ${email}?`)) return;
    setBusyId(`em-${userId}`);
    try {
      const res = await authFetch(`${API}/admin/signed-agreements/${userId}/resend`, { method: "POST" });
      if (res.ok) toast.success(`Sent to ${email}`);
      else toast.error("Re-send failed");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = rows.filter(r => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [r.name, r.email, r.phone, r.ndis_number].some(v => (v || "").toLowerCase().includes(q));
  });

  if (loading) return <p className="text-slate-400 p-6">Loading signed agreements…</p>;

  return (
    <div className="space-y-4" data-testid="admin-signed-agreements">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">Digitally signed agreements</h3>
          <p className="text-xs text-slate-500">{rows.length} participant{rows.length === 1 ? "" : "s"} have e-signed · click to download a PDF copy or resend it to the participant.</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, NDIS #"
            className="h-9 w-64 pl-9 text-sm"
            data-testid="signed-agreements-search"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-8 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No signed agreements {query ? "match your search" : "yet"}.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl border border-slate-100 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Participant</th>
                <th className="px-4 py-3">NDIS</th>
                <th className="px-4 py-3">Signed on</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(r => (
                <tr key={r.user_id} data-testid={`signed-row-${r.user_id}`} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700 font-medium">{r.ndis_number || <span className="text-slate-400">—</span>}</div>
                    <div className="text-xs text-slate-500 capitalize">{(r.ndis_plan_type || "—").replace(/_/g, " ")}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {r.signed_at ? new Date(r.signed_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      Signed as: <span className="font-medium text-slate-600">{r.signed_name || "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className="bg-slate-100 text-slate-700 font-mono text-[11px]">{r.version || "—"}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        className="h-8 bg-[#87CEEB] hover:bg-[#6BB8DB] text-white"
                        disabled={busyId === `dl-${r.user_id}`}
                        onClick={() => download(r.user_id, r.name)}
                        data-testid={`signed-download-${r.user_id}`}
                      >
                        {busyId === `dl-${r.user_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> PDF</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        disabled={busyId === `em-${r.user_id}` || !r.email}
                        onClick={() => resendEmail(r.user_id, r.email)}
                        data-testid={`signed-resend-${r.user_id}`}
                      >
                        {busyId === `em-${r.user_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Re-send</>}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminServiceAgreementsReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/admin/service-agreements`);
    if (res.ok) setRows(await res.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (userId, action) => {
    if (action === "revoke" && !window.confirm("Revoke this participant's service agreement approval? Workers will not be able to accept their shifts until re-approved.")) return;
    setBusyId(userId);
    const res = await authFetch(`${API}/admin/service-agreements/${userId}`, { method: "PUT", body: JSON.stringify({ action }) });
    if (res.ok) { toast.success(`Service agreement ${action}d`); await load(); }
    else { toast.error("Action failed"); }
    setBusyId(null);
  };

  if (loading) return <p className="text-slate-400 p-6">Loading…</p>;
  if (rows.length === 0) return <p className="text-slate-500 p-6">No participant bookings yet — service agreements will appear here once a participant requests their first booking.</p>;

  const pending = rows.filter(r => !r.service_agreement_approved);
  const approved = rows.filter(r => r.service_agreement_approved);

  const Row = ({ r }) => (
    <div key={r.user_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid={`esa-row-${r.user_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-bold text-slate-900">{r.name}</h3>
          <p className="text-xs text-slate-500">{r.email} · {r.phone || "no phone"} · {(r.ndis_plan_type || "unspecified").replace(/_/g,' ')}</p>
          <p className="text-xs text-slate-500 mt-1">
            Pending bookings: <span className="font-medium text-slate-700">{r.pending_bookings}</span>
            {r.latest_booking_at && <> · Last booked {new Date(r.latest_booking_at).toLocaleDateString()}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={r.service_agreement_approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
            {r.service_agreement_approved ? "AGREEMENT APPROVED" : "AWAITING SIGNED AGREEMENT"}
          </Badge>
          {r.service_agreement_approved ? (
            <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600" disabled={busyId === r.user_id} onClick={() => setStatus(r.user_id, "revoke")} data-testid={`esa-revoke-${r.user_id}`}>
              Revoke
            </Button>
          ) : (
            <Button size="sm" className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" disabled={busyId === r.user_id} onClick={() => setStatus(r.user_id, "approve")} data-testid={`esa-approve-${r.user_id}`}>
              Mark agreement received & approve
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-service-agreements">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <p className="font-semibold mb-1">How this works</p>
        <p>The service agreement is emailed manually to each participant after their first booking. Once they return the signed copy, mark it as approved here. Workers cannot accept any shift until you approve the participant's signed agreement.</p>
      </div>

      {pending.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Awaiting approval ({pending.length})</h3>
          {pending.map(r => <Row key={r.user_id} r={r} />)}
        </div>
      )}

      {approved.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Approved ({approved.length})</h3>
          {approved.map(r => <Row key={r.user_id} r={r} />)}
        </div>
      )}
    </div>
  );
}

function AdminIncidentsReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/admin/incidents`);
    if (res.ok) setItems(await res.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    const notes = window.prompt("Add admin notes (optional)") || "";
    const res = await authFetch(`${API}/admin/incidents/${id}`, { method: "PUT", body: JSON.stringify({ status, admin_notes: notes }) });
    if (res.ok) { toast.success("Incident updated"); load(); }
    else { toast.error("Update failed"); }
  };

  const sevColor = (s) => ({
    low: "bg-slate-100 text-slate-700",
    medium: "bg-amber-100 text-amber-700",
    high: "bg-orange-100 text-orange-700",
    critical: "bg-red-100 text-red-700"
  }[s] || "bg-slate-100");

  if (loading) return <p className="text-slate-400 p-6">Loading…</p>;
  if (items.length === 0) return <p className="text-slate-500 p-6">No incidents reported.</p>;

  return (
    <div className="space-y-3" data-testid="admin-incidents-review">
      {items.map(it => (
        <div key={it.incident_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="font-bold text-slate-900">{it.category} <span className="text-slate-400 font-normal">· {it.incident_id}</span></p>
              <p className="text-xs text-slate-500">{it.reporter_name} ({it.reporter_type}) · {new Date(it.created_at).toLocaleString()}</p>
            </div>
            <div className="flex gap-2">
              <Badge className={sevColor(it.severity)}>{it.severity}</Badge>
              <Badge className={it.status === "open" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}>{it.status}</Badge>
            </div>
          </div>
          <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 mb-3 whitespace-pre-wrap">{it.description}</p>
          {it.admin_notes && <p className="text-xs text-slate-500 mb-2">Admin notes: {it.admin_notes}</p>}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => update(it.incident_id, "investigating")}>Mark Investigating</Button>
            <Button size="sm" variant="outline" onClick={() => update(it.incident_id, "resolved")}>Mark Resolved</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminCallbackRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | all | new | contacted | resolved | archived
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/admin/support-requests`);
    if (res.ok) setItems(await res.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    setBusy(id);
    const res = await authFetch(`${API}/admin/support-requests/${id}`, { method: "PUT", body: JSON.stringify(patch) });
    if (res.ok) { toast.success("Updated"); await load(); }
    else { toast.error("Update failed"); }
    setBusy(null);
  };

  const statusColor = (s) => ({
    new: "bg-amber-100 text-amber-800",
    contacted: "bg-blue-100 text-blue-700",
    resolved: "bg-emerald-100 text-emerald-700",
    archived: "bg-slate-100 text-slate-600",
  }[s || "new"] || "bg-slate-100 text-slate-700");

  const sourceLabel = (it) => {
    if (it.source === "landing_page_lead_form") return "Landing page lead form";
    if (it.user_id) return "Logged-in participant request";
    return "Public request";
  };

  const filtered = items.filter(it => {
    const s = it.status || "new";
    if (filter === "all") return true;
    if (filter === "active") return s !== "resolved" && s !== "archived";
    return s === filter;
  });

  const counts = {
    active: items.filter(it => (it.status || "new") !== "resolved" && (it.status || "new") !== "archived").length,
    new: items.filter(it => (it.status || "new") === "new").length,
    contacted: items.filter(it => it.status === "contacted").length,
    resolved: items.filter(it => it.status === "resolved").length,
    all: items.length,
  };

  if (loading) return <p className="text-slate-400 p-6">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="admin-callback-requests">
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex flex-wrap gap-2">
          {[
            { id: "active", label: "Active" },
            { id: "new", label: "New" },
            { id: "contacted", label: "Contacted" },
            { id: "resolved", label: "Resolved" },
            { id: "all", label: "All" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                filter === t.id ? "bg-[#87CEEB] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              data-testid={`callback-filter-${t.id}`}
            >
              {t.label} <span className="opacity-70">· {counts[t.id] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-slate-500">
          No callback requests {filter !== "all" ? `with status "${filter}"` : "yet"}.
        </div>
      ) : filtered.map(it => {
        const status = it.status || "new";
        return (
          <div key={it.request_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid={`callback-row-${it.request_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-slate-900">{it.user_name || "Anonymous"}</h3>
                  <Badge className={statusColor(status)}>{status.toUpperCase()}</Badge>
                  <span className="text-[11px] text-slate-400 font-mono">{it.request_id}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {sourceLabel(it)} · {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {it.phone && (
                  <a href={`tel:${it.phone}`} className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 font-medium hover:bg-emerald-200" data-testid={`call-${it.request_id}`}>
                    Call {it.phone}
                  </a>
                )}
                {it.user_email && (
                  <a href={`mailto:${it.user_email}`} className="text-xs px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 font-medium hover:bg-blue-200">
                    Email {it.user_email}
                  </a>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-700 mb-3">
              {it.service_type && <div><span className="text-slate-500">Service:</span> {it.service_type}</div>}
              {(it.preferred_date || it.preferred_time) && (
                <div><span className="text-slate-500">When:</span> {it.preferred_date} {it.preferred_time} {it.frequency && `(${it.frequency.replace(/_/g, " ")})`}</div>
              )}
              {it.preferred_contact && <div><span className="text-slate-500">Contact via:</span> {it.preferred_contact}</div>}
            </div>

            {it.notes && (
              <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap mb-3">{it.notes}</p>
            )}

            {it.admin_notes && (
              <p className="text-xs text-slate-500 mb-3">Admin notes: {it.admin_notes}</p>
            )}

            <div className="flex flex-wrap gap-2">
              {status !== "contacted" && (
                <Button size="sm" variant="outline" disabled={busy === it.request_id} onClick={() => update(it.request_id, { status: "contacted" })} data-testid={`callback-contacted-${it.request_id}`}>
                  Mark Contacted
                </Button>
              )}
              {status !== "resolved" && (
                <Button size="sm" variant="outline" disabled={busy === it.request_id} onClick={() => update(it.request_id, { status: "resolved" })} data-testid={`callback-resolved-${it.request_id}`}>
                  Mark Resolved
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={busy === it.request_id}
                onClick={() => {
                  const notes = window.prompt("Admin notes (visible to admins only)", it.admin_notes || "");
                  if (notes !== null) update(it.request_id, { admin_notes: notes });
                }}
              >
                {it.admin_notes ? "Edit notes" : "Add notes"}
              </Button>
              {status !== "archived" && (
                <Button size="sm" variant="outline" className="text-slate-500" disabled={busy === it.request_id} onClick={() => update(it.request_id, { status: "archived" })}>
                  Archive
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AdminUsersDirectory() {
  const [data, setData] = useState({ users: [], total: 0, counts: { all: 0, participant: 0, worker: 0, admin: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | participant | worker | admin
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [detailUser, setDetailUser] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const params = new URLSearchParams({ limit: "500" });
      if (filter !== "all") params.set("user_type", filter);
      if (debounced.trim()) params.set("q", debounced.trim());
      const res = await authFetch(`${API}/admin/users?${params.toString()}`);
      if (res.ok) setData(await res.json());
      setLoading(false);
    };
    load();
  }, [filter, debounced]);

  const exportCsv = () => {
    const rows = data.users.map(u => [
      u.name || "",
      u.email || "",
      (u.user_type || "").replace(/_/g, " "),
      u.phone || "",
      u.location || "",
      u.state || "",
      u.dob || "",
      u.ndis_number || "",
      u.ndis_plan_type || "",
      u.plan_manager_name || "",
      u.plan_manager_email || "",
      u.emergency_contact_name || "",
      u.emergency_contact_phone || "",
      u.email_verified ? "verified" : "unverified",
      u.profile_completed ? "yes" : "no",
      u.service_agreement_approved ? "yes" : "no",
      u.created_at ? new Date(u.created_at).toISOString() : ""
    ]);
    const header = ["Name","Email","Type","Phone","Location","State","DOB","NDIS Number","NDIS Plan","Plan Manager Name","Plan Manager Email","Emergency Contact","Emergency Phone","Email Verified","Onboarded","ESA Approved","Signup Method","Signed up"];
    const csv = [header, ...rows.map((r, i) => [...r.slice(0, 15), data.users[i].auth_method === "google" ? "google" : "email", r[15]])]
      .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `btr-users-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const counts = data.counts || { all: 0, participant: 0, worker: 0, admin: 0 };

  const tabs = [
    { id: "all", label: "All", count: counts.all },
    { id: "participant", label: "Participants", count: counts.participant },
    { id: "worker", label: "Support Workers", count: counts.worker },
    { id: "admin", label: "Admins", count: counts.admin },
  ];

  const typeBadge = (t) => {
    const cls = t === "participant" ? "bg-blue-100 text-blue-700"
      : t === "admin" ? "bg-slate-200 text-slate-700"
      : "bg-emerald-100 text-emerald-700";
    return <Badge className={cls}>{(t || "user").replace(/_/g, " ")}</Badge>;
  };

  const onboardedBadge = (u) =>
    u.profile_completed
      ? <span className="text-xs font-medium text-emerald-700">Onboarded</span>
      : <span className="text-xs font-medium text-amber-700">Pending</span>;

  const deleteUser = async (u) => {
    const confirmMsg = `Delete ${u.name || u.email}?\n\nThis permanently removes the account and ALL their bookings, messages, reviews, documents, and notifications. This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setDeletingId(u.user_id);
    try {
      const res = await authFetch(`${API}/admin/users/${u.user_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success(`Deleted ${u.email}`);
        // Optimistic — reload the list
        const params = new URLSearchParams({ limit: "500" });
        if (filter !== "all") params.set("user_type", filter);
        if (debounced.trim()) params.set("q", debounced.trim());
        const r = await authFetch(`${API}/admin/users?${params.toString()}`);
        if (r.ok) setData(await r.json());
      } else {
        let msg = "Delete failed";
        try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch (_) {}
        toast.error(msg);
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-directory">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl shadow-sm p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  filter === t.id ? "bg-[#87CEEB] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                data-testid={`users-filter-${t.id}`}
              >
                {t.label} <span className="opacity-70">· {t.count}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <AdminUserCreator onCreated={() => setDebounced(q + " ")} />
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data.users.length} data-testid="users-export-csv">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, phone or location…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10"
            data-testid="users-search-input"
          />
        </div>
      </div>

      {/* Result */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between text-sm">
          <span className="text-slate-700 font-medium">
            {loading ? "Loading…" : `${data.total} ${data.total === 1 ? "user" : "users"}`}
            {q ? <span className="text-slate-400"> matching "{q}"</span> : null}
          </span>
          <span className="text-xs text-slate-400">Newest signups first</span>
        </div>
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : data.users.length === 0 ? (
          <p className="p-8 text-center text-slate-500">No users match this filter.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-500">Name</th>
                  <th className="text-left p-3 font-medium text-slate-500">Contact</th>
                  <th className="text-left p-3 font-medium text-slate-500">Type</th>
                  <th className="text-left p-3 font-medium text-slate-500">Location</th>
                  <th className="text-left p-3 font-medium text-slate-500">Onboarded</th>
                  <th className="text-left p-3 font-medium text-slate-500">Signed up via</th>
                  <th className="text-left p-3 font-medium text-slate-500">Signed up</th>
                  <th className="text-right p-3 font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.user_id || u.email} className="border-t hover:bg-slate-50/60">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#87CEEB]/20 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-[#87CEEB] font-medium text-sm">{u.name?.charAt(0) || "?"}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{u.name || "—"}</div>
                          {u.user_type === "participant" && (
                            <div className="text-[11px] text-slate-500 capitalize">
                              {(u.ndis_plan_type || "").replace(/_/g, " ") || <span className="text-slate-400">no plan type</span>}
                              {u.ndis_number ? <span className="text-slate-400"> · NDIS #{u.ndis_number}</span> : null}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-slate-600">
                      <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" /> <span className="truncate">{u.email}</span></div>
                      {u.phone && <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1"><Phone className="w-3 h-3 text-slate-400" /> {u.phone}</div>}
                    </td>
                    <td className="p-3">{typeBadge(u.user_type)}</td>
                    <td className="p-3 text-slate-600">{u.location || "—"}</td>
                    <td className="p-3">{onboardedBadge(u)}</td>
                    <td className="p-3">
                      {u.auth_method === "google" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold border border-blue-100">
                          <svg className="w-3 h-3" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                          </svg>
                          Google
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold">
                          <Mail className="w-3 h-3" /> Email
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-slate-500 whitespace-nowrap">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDetailUser(u)}
                          className="h-8 text-xs"
                          data-testid={`view-user-${u.user_id}`}
                          title="View full contact & NDIS details"
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={deletingId === u.user_id || u.user_type === "admin"}
                          onClick={() => deleteUser(u)}
                          className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                          data-testid={`delete-user-${u.user_id}`}
                          title={u.user_type === "admin" ? "Admins cannot be deleted from the UI" : "Permanently delete this user and their data"}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <UserDetailModal open={!!detailUser} user={detailUser} onClose={() => setDetailUser(null)} />
    </div>
  );
}

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [pendingWorkers, setPendingWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [rejectDialog, setRejectDialog] = useState({ open: false, worker: null });
  const [rejectReason, setRejectReason] = useState("");
  const [processingWorker, setProcessingWorker] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, pendingRes] = await Promise.all([
        authFetch(`${API}/admin/stats`),
        authFetch(`${API}/admin/bookings?limit=20`),
        authFetch(`${API}/admin/workers/pending`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setBookings(data.bookings);
      }
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingWorkers(data);
      }
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await authFetch(`${API}/admin/bookings/${bookingId}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(`Booking ${newStatus}`);
        fetchData();
      }
    } catch (error) {
      console.error("Failed to update booking:", error);
      toast.error("Failed to update booking");
    }
  };

  const handleVerifyWorker = async (workerId, action) => {
    setProcessingWorker(workerId);
    try {
      const response = await authFetch(`${API}/admin/workers/${workerId}/verify`, {
        method: "PUT",
        body: JSON.stringify({ 
          action,
          reason: action === "reject" ? rejectReason : ""
        })
      });
      
      if (response.ok) {
        toast.success(action === "approve" ? "Worker approved!" : "Worker rejected");
        fetchData();
        setRejectDialog({ open: false, worker: null });
        setRejectReason("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update worker");
      }
    } catch (error) {
      toast.error("Failed to update worker");
    } finally {
      setProcessingWorker(null);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-6 shadow-sm"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-500 text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold text-slate-900">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#87CEEB]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <img src={LOGO_ICON} alt="BTR Health" className="h-7" />
              </Link>
              <h1 className="text-lg font-semibold text-white">Admin Dashboard</h1>
            </div>
            <Link to="/dashboard">
              <Button size="sm" variant="outline" className="text-white border-white/20 hover:bg-white/10">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-6xl">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 rounded-xl p-1 mb-6 flex flex-wrap gap-1 h-auto justify-start w-full">
            <TabsTrigger value="overview" className="rounded-lg">Overview</TabsTrigger>
            <TabsTrigger value="verification" className="rounded-lg relative">
              Verification
              {pendingWorkers.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingWorkers.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg">Users</TabsTrigger>
            <TabsTrigger value="applications" className="rounded-lg" data-testid="tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-lg">Bookings</TabsTrigger>
            <TabsTrigger value="documents" className="rounded-lg">Worker Docs</TabsTrigger>
            <TabsTrigger value="participants" className="rounded-lg">Participant Docs</TabsTrigger>
            <TabsTrigger value="id-verifications" className="rounded-lg" data-testid="tab-id-verifications">ID Verifications</TabsTrigger>
            <TabsTrigger value="agreements" className="rounded-lg" data-testid="tab-service-agreements">Service Agreements</TabsTrigger>
            <TabsTrigger value="pricing" className="rounded-lg" data-testid="tab-pricing">Services &amp; Pricing</TabsTrigger>
            <TabsTrigger value="callbacks" className="rounded-lg" data-testid="tab-callbacks">Callback Requests</TabsTrigger>
            <TabsTrigger value="incidents" className="rounded-lg">Incidents</TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="finance" className="rounded-lg" data-testid="tab-finance">Finance</TabsTrigger>
            <TabsTrigger value="invoicing" className="rounded-lg" data-testid="tab-invoicing">Invoicing</TabsTrigger>
            <TabsTrigger value="agreements" className="rounded-lg" data-testid="tab-agreements">Agreements</TabsTrigger>
            <TabsTrigger value="guide" className="rounded-lg" data-testid="tab-guide">Operations Guide</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {stats && (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <StatCard
                    title="Total Users"
                    value={stats.users.total}
                    icon={Users}
                    color="bg-blue-500"
                    subtitle={`${stats.users.participants} participants, ${stats.users.workers} workers`}
                  />
                  <StatCard
                    title="Total Bookings"
                    value={stats.bookings.total}
                    icon={Calendar}
                    color="bg-emerald-500"
                    subtitle={`${stats.bookings.confirmed} confirmed`}
                  />
                  <StatCard
                    title="Reviews"
                    value={stats.reviews}
                    icon={Star}
                    color="bg-amber-500"
                  />
                  <StatCard
                    title="Revenue"
                    value={`$${stats.revenue.toFixed(2)}`}
                    icon={DollarSign}
                    color="bg-purple-500"
                    subtitle="From paid bookings"
                  />
                </div>

                {/* Booking Status Breakdown */}
                <div className="bg-white rounded-2xl p-6 shadow-sm mb-8">
                  <h3 className="font-semibold text-slate-900 mb-4">Booking Status</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-amber-50 rounded-xl">
                      <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-amber-600">{stats.bookings.pending}</p>
                      <p className="text-sm text-amber-600">Pending</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-xl">
                      <TrendingUp className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-600">{stats.bookings.confirmed}</p>
                      <p className="text-sm text-blue-600">Confirmed</p>
                    </div>
                    <div className="text-center p-4 bg-emerald-50 rounded-xl">
                      <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-emerald-600">{stats.bookings.completed}</p>
                      <p className="text-sm text-emerald-600">Completed</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <XCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-slate-500">{stats.bookings.total - stats.bookings.pending - stats.bookings.confirmed - stats.bookings.completed}</p>
                      <p className="text-sm text-slate-500">Cancelled</p>
                    </div>
                  </div>
                </div>

                {/* DANGER ZONE — Reset to Launch State */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-red-200 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900">Delete Google sign-ups only</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Removes every user who signed in via Google (keeps admin + the 10 worker logins). Cascades through their bookings, messages, conversations and notifications.
                      </p>
                      <Button
                        onClick={async () => {
                          const ok = window.confirm("Delete all Google sign-up accounts? This cannot be undone.");
                          if (!ok) return;
                          try {
                            const r = await authFetch(`${API}/admin/delete-google-accounts`, { method: "POST" });
                            const data = await r.json().catch(() => ({}));
                            if (r.ok) {
                              toast.success(data.message || "Deleted");
                              setTimeout(() => window.location.reload(), 800);
                            } else {
                              toast.error(data.detail || "Failed");
                            }
                          } catch (e) {
                            toast.error("Request failed");
                          }
                        }}
                        variant="outline"
                        className="mt-3 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-400"
                        data-testid="admin-delete-google-accounts"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Google sign-ups
                      </Button>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-4 flex items-start gap-4">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900">Reset to clean launch state</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Wipes <strong>all</strong> bookings, messages, reviews, callback requests, incidents, documents and non-admin users. Reseeds only your 10 launch worker profiles + admin + worker logins. Use this once after redeploying to clear production mock data.
                      </p>
                      <Button
                        onClick={async () => {
                          const confirmText = window.prompt("Type RESET to confirm. This cannot be undone.");
                          if (confirmText !== "RESET") return;
                          try {
                            const r = await authFetch(`${API}/seed`, { method: "POST" });
                            const data = await r.json().catch(() => ({}));
                            if (r.ok) {
                              toast.success(data.message || "Reset complete");
                              setTimeout(() => window.location.reload(), 800);
                            } else {
                              toast.error(data.detail || "Reset failed");
                            }
                          } catch (e) {
                            toast.error("Reset failed — check connection");
                          }
                        }}
                        variant="outline"
                        className="mt-3 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-400"
                        data-testid="admin-reset-launch-state"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Reset to launch state
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="verification">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#87CEEB]" />
                  <h3 className="font-semibold text-slate-900">Worker Verification</h3>
                </div>
                <Badge className="bg-amber-100 text-amber-700">
                  {pendingWorkers.length} pending
                </Badge>
              </div>
              
              {pendingWorkers.length === 0 ? (
                <div className="p-12 text-center">
                  <UserCheck className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                  <h4 className="font-semibold text-slate-900 mb-2">All Caught Up!</h4>
                  <p className="text-slate-500">No workers pending verification</p>
                </div>
              ) : (
                <div className="divide-y">
                  {pendingWorkers.map((worker) => (
                    <div key={worker.worker_id} className="p-4">
                      <div className="flex items-start gap-4">
                        <img
                          src={worker.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(worker.name)}&background=87CEEB&color=fff`}
                          alt={worker.name}
                          className="w-16 h-16 rounded-xl object-cover"
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-slate-900">{worker.name}</h4>
                              <p className="text-sm text-slate-500">{worker.email}</p>
                              <p className="text-sm text-slate-500">{worker.location}</p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className="px-2 py-1 bg-[#87CEEB]/10 text-[#87CEEB] rounded text-xs font-medium">
                              ${worker.hourly_rate}/hr
                            </span>
                            {worker.services?.slice(0, 3).map((service) => (
                              <span key={service} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                                {service.replace(/_/g, " ")}
                              </span>
                            ))}
                          </div>
                          
                          {worker.verification_docs?.length > 0 && (
                            <div className="flex items-center gap-2 mb-3 text-sm text-slate-600">
                              <FileText className="w-4 h-4" />
                              <span>{worker.verification_docs.length} documents uploaded</span>
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600"
                              onClick={() => handleVerifyWorker(worker.worker_id, "approve")}
                              disabled={processingWorker === worker.worker_id}
                            >
                              {processingWorker === worker.worker_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => setRejectDialog({ open: true, worker })}
                              disabled={processingWorker === worker.worker_id}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <AdminUsersDirectory />
          </TabsContent>

          <TabsContent value="applications">
            <AdminApplicationsManager />
          </TabsContent>

          <TabsContent value="bookings">
            <AdminBookingsManager />
          </TabsContent>

          <TabsContent value="documents">
            <AdminDocumentsReview />
          </TabsContent>

          <TabsContent value="participants">
            <AdminParticipantDocsReview />
          </TabsContent>

          <TabsContent value="id-verifications">
            <AdminIdVerifications />
          </TabsContent>

          <TabsContent value="pricing">
            <AdminPricingReference />
          </TabsContent>

          <TabsContent value="agreements">
            <div className="space-y-8">
              <AdminSignedAgreementsList />
              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-base font-bold text-slate-900 mb-1">Bookings · approval ledger</h3>
                <p className="text-xs text-slate-500 mb-4">For older / paper-signed agreements that you confirmed manually. The digital flow above auto-approves on signing.</p>
                <AdminServiceAgreementsReview />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="callbacks">
            <AdminCallbackRequests />
          </TabsContent>

          <TabsContent value="incidents">
            <AdminIncidentsManager />
          </TabsContent>
          <TabsContent value="analytics">
            <AdminAnalytics />
          </TabsContent>

          <TabsContent value="finance">
            <AdminFinance />
          </TabsContent>
          <TabsContent value="invoicing">
            <AdminInvoicingManager />
          </TabsContent>
          <TabsContent value="agreements">
            <AdminServiceAgreementBuilder />
          </TabsContent>

          <TabsContent value="guide">
            <OperationsGuide />
          </TabsContent>
        </Tabs>
      </main>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Worker</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting {rejectDialog.worker?.name}'s application.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Textarea
              placeholder="Reason for rejection (e.g., incomplete documents, invalid certifications)..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setRejectDialog({ open: false, worker: null });
                setRejectReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-500 hover:bg-red-600"
              onClick={() => handleVerifyWorker(rejectDialog.worker?.worker_id, "reject")}
              disabled={processingWorker === rejectDialog.worker?.worker_id}
            >
              {processingWorker === rejectDialog.worker?.worker_id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject Worker
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
